import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { CheckCircle, Crown, Loader2, ArrowRight } from 'lucide-react';
import confetti from 'canvas-confetti';

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  const verifyPayment = useCallback(async (sessionId) => {
    try {
      const response = await api.get(`/subscription/verify/${sessionId}`);
      setStatus(response.data.status);
      
      if (response.data.status === 'success') {
        await refreshUser();
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        toast.success('Bienvenue dans Premium !');
      }
    } catch (error) {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  }, [api, refreshUser]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      setLoading(false);
      setStatus('error');
    }
  }, [searchParams, verifyPayment]);

  if (loading) {
    return (
      <div className="min-h-screen tropic-bg flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Vérification du paiement...</p>
        </div>
      </div>
    );
  }

  if (status !== 'success') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="h-16 w-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
              <Crown className="h-8 w-8 text-yellow-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Paiement en attente</h1>
            <p className="text-muted-foreground mb-6">
              {status === 'pending' 
                ? 'Votre paiement est en cours de traitement. Vous recevrez une notification dès qu\'il sera confirmé.'
                : 'Une erreur est survenue lors de la vérification. Contactez le support si le problème persiste.'}
            </p>
            <Button onClick={() => navigate('/feed')} className="w-full">
              Retour au fil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-r from-primary to-primary/80 p-8 text-center text-white">
          <div className="h-20 w-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-10 w-10" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Bienvenue Premium !</h1>
          <p className="text-white/80">Votre abonnement est maintenant actif</p>
        </div>
        
        <CardContent className="p-6 space-y-6">
          <div className="space-y-3">
            <h3 className="font-semibold">Vos avantages Premium :</h3>
            <ul className="space-y-2">
              {[
                { id: 'unlimited', text: 'Posts illimités' },
                { id: 'stats', text: 'Statistiques avancées' },
                { id: 'badge', text: 'Badge Premium vérifié' },
                { id: 'support', text: 'Support prioritaire' },
                { id: 'boost', text: 'Boost gratuit mensuel' }
              ].map((feature) => (
                <li key={feature.id} className="flex items-center gap-2 text-sm">
                  <Crown className="h-4 w-4 text-primary flex-shrink-0" />
                  <span>{feature.text}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/settings/stats')} 
              className="w-full"
              data-testid="view-stats-btn"
            >
              Voir mes statistiques
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/feed')} 
              className="w-full"
            >
              Retour au fil
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccessPage;
